import type { RunResult } from "@/api/adapt";
import type { HeadlineValueDto, NodeChangeRowDto } from "@/api/types";
import { fmtValue, pct } from "@/lib/format";
import { headline, heroDate } from "@/lib/results";
import type { ModelNode, MetricNode, Scenario } from "@/model/types";

/** A node's headline number as text ("28%", "41 people"), or "…". */
export function headlineText(n: ModelNode, r: RunResult | null | undefined): string {
	if (!r) return "…";
	const v = headline(n, r, heroDate(n));
	return n.kind === "event" ? pct(v) : fmtValue(v, (n as MetricNode).unit);
}

/** An API headline value as text: "28%" or "$2.33M". */
export function headlineValueText(h: HeadlineValueDto | null | undefined, unit?: string | null): string {
	if (!h) return "…";
	return h.kind === "event" ? pct(h.chance) : fmtValue(h.likely, unit ?? "");
}

/** "Chance: 7%", "Hypothetical", "Impossible" or "Nothing set yet", from the API's scenario figures. */
export function chanceLabel(
	sc: Pick<Scenario, "levers"> | { lever_count: number },
	r: { probability: number | null; hypothetical: boolean; impossible: boolean } | null | undefined,
): string | null {
	const count = "levers" in sc ? sc.levers.length : sc.lever_count;
	if (!count) return "Nothing set yet";
	if (!r) return null;
	if (r.impossible) return "Impossible";
	if (r.hypothetical) return "Hypothetical";
	return `Chance: ${pct(r.probability)}`;
}

/** "Layoffs needed 28% → 100%": one change against Baseline from the API. */
export function changeText(c: Pick<NodeChangeRowDto, "kind" | "unit" | "baseline" | "scenario">): string {
	const f = (v: number) => (c.kind === "event" ? pct(v) : fmtValue(v, c.unit ?? ""));
	return `${f(c.baseline)} → ${f(c.scenario)}`;
}

export function setsText(sc: Scenario): string {
	const k = new Set(sc.levers.map((l) => l.node)).size;
	return k ? `sets ${k} ${k === 1 ? "node" : "nodes"}` : "nothing set yet";
}
